import uniqBy from "lodash.uniqby";
import polyline from "google-polyline";

import { isAdmin, isCompanyUser, isMasterdata, isTerminal, isCustomer, isReadOnly } from "components/utils/Permission";
import { hasPluginPermission } from "imports/Utils";

import { Session } from "ezStore";
import { Location, LocationType } from "__generated__/graphql";
import { OpeningHoursFragment } from "__generated__/view/relations/graphql/relation.fragments.generated";
import moment from "moment";

export const MAP_HEIGHT = "calc(100vh - 220px)";

export enum LocationTabs {
	DETAILS = "details",
	MAP = "map",
	OPENING_HOURS = "openingHours",
	RELATIONS = "relations",
	QUAYS = "quays",
	DOCUMENTS = "documents",
	CONTACTS = "contacts"
}

export enum LocationQuayTabs {
	BOLDERS = "bolders",
	CRANES = "cranes"
}

export const hasEditLocationPermission = (session: Session) => {
	return !isReadOnly(session) && (
		isAdmin(session)
		|| isCompanyUser(session)
		|| isMasterdata(session)
		|| isTerminal(session)
		|| (isCustomer(session) && hasPluginPermission(session, "customerAddLocations"))
	);
};

export const cleanlyDecodePolyline = (encodedPolyline?: string) => {
	if (!encodedPolyline) {
		return [ { line: [] } ];
	}
	const rest = { color: "red", fillColor: "#ff848482", fillOpacity: 1 };
	return [ { ...rest, line: uniqBy(polyline.decode(encodedPolyline).map(([ lat, lng ]) => ({ lat, lng })), JSON.stringify) } ];
};

export const getLocationTypeNumber = (type: LocationType) => {
	switch (type) {
		case LocationType.Terminal:
			return 1;
		case LocationType.Address:
			return 2;
		case LocationType.Sluice:
			return 3;
		case LocationType.Port:
			return 4;
		case LocationType.WaitingArea:
			return 5;
		case LocationType.Depot:
			return 6;
		case LocationType.ScopeIn:
			return 7;
		case LocationType.ScopeOut:
			return 8;
		case LocationType.Warehouse:
			return 9;
		case LocationType.BorderCrossing:
			return 10;
		case LocationType.InlandTerminal:
			return 11;
		case LocationType.Area:
			return 12;
		case LocationType.BunkerStation:
			return 13;
		case LocationType.Waterway:
			return 14;
		case LocationType.NotificationArea:
			return 15;
		case LocationType.ExtractionMachine:
			return 16;
		case LocationType.Wharf:
			return 17;
		case LocationType.Buoy:
			return 18;
		default:
			return 1;
	}
};

export const getLocationTypeByNumber = (type: number) => {
	switch (type) {
		case 1:
			return LocationType.Terminal;
		case 2:
			return LocationType.Address;
		case 3:
			return LocationType.Sluice;
		case 4:
			return LocationType.Port;
		case 5:
			return LocationType.WaitingArea;
		case 6:
			return LocationType.Depot;
		case 7:
			return LocationType.ScopeIn;
		case 8:
			return LocationType.ScopeOut;
		case 9:
			return LocationType.Warehouse;
		case 10:
			return LocationType.BorderCrossing;
		case 11:
			return LocationType.InlandTerminal;
		case 12:
			return LocationType.Area;
		case 13:
			return LocationType.BunkerStation;
		case 14:
			return LocationType.Waterway;
		case 15:
			return LocationType.NotificationArea;
		case 16:
			return LocationType.ExtractionMachine;
		case 17:
			return LocationType.Wharf;
		case 18:
			return LocationType.Buoy;
		default:
			return LocationType.Terminal;
	}
};

export const getLocationTypeColor = (type: LocationType) => {
	switch (type) {
		case LocationType.Terminal:
			return "#F24853";
		case LocationType.Address:
			return "#F24853";
		case LocationType.Sluice:
			return "#8AB9B9";
		case LocationType.Port:
			return "#73A7E8";
		case LocationType.WaitingArea:
			return "#8AB9B9";
		case LocationType.Depot:
			return "#73A7E8";
		case LocationType.ScopeIn:
			return "#F24853";
		case LocationType.ScopeOut:
			return "#F24853";
		case LocationType.Warehouse:
			return "#F24853";
		case LocationType.BorderCrossing:
			return "#8AB9B9";
		case LocationType.InlandTerminal:
			return "#73A7E8";
		case LocationType.Area:
			return "#7A7A7A";
		case LocationType.BunkerStation:
			return "#8AB9B9";
		case LocationType.Waterway:
			return "#F7AF9D";
		default:
			return "#7A7A7A";
	}
};

type GetLocationGroupParam = Pick<Location, "description"> & {
		locationGroups?: Pick<Location, "description" | "type">[] | null
}
export const locationDescriptionWithGroup = (location: GetLocationGroupParam | null | undefined) => {
	const port = location?.locationGroups?.find(x => x.type == LocationType.Port)?.description;
	return port ? `${port} • ${location.description}` : location?.description ?? "";
};

export const useLocationFieldPermission = (locationType: LocationType | null) => {

	const excludedTypes = new Set([
		LocationType.Area,
		LocationType.Address,
		LocationType.BorderCrossing,
		LocationType.ScopeIn,
		LocationType.ScopeOut,
		LocationType.Waterway,
		LocationType.Sluice,
		LocationType.NotificationArea,
	]);

	return {
		hasPreHandlingTimeMinPermission: locationType ? !excludedTypes.has(locationType) : false,
		hasPostHandlingTimeMinPermission: locationType ? !excludedTypes.has(locationType) : false,
		hasLoadingDepthPermission: locationType ? !excludedTypes.has(locationType) : false,
		hasMaxShipLengthPermission: locationType ? !excludedTypes.has(locationType) : false,
		hasMaxShipDraughtPermission: locationType ? !excludedTypes.has(locationType) : false,
		hasMaxShipWidthPermission: locationType ? !excludedTypes.has(locationType) : false,
	};
};

export function getOpenHoursWindow(openingHours: OpeningHoursFragment | null | undefined, date: number) {
	if (openingHours) {
		const currentDay = moment(date).isoWeekday();
		const days = [ "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday" ];

		const fromMinutes = openingHours[`${days[currentDay]}From` as keyof OpeningHoursFragment];
		const toMinutes = openingHours[`${days[currentDay]}To` as keyof OpeningHoursFragment];
		const fromDateTime = moment(date).startOf("day").add(fromMinutes, "minutes").unix() * 1000;
		const toDateTime = moment(date).startOf("day").add(toMinutes, "minutes").unix() * 1000;

		return { from: Number(fromDateTime), to: Number(toDateTime) };
	}
}

